import { useEffect, useRef, useState } from 'react';

/* ────────────────────────────────────────────────────────────
 * useCountUp — animate a numeric value from 0 → target on mount.
 *
 * MOTION.md §4.9:
 *   - 600ms with --ease-out-quart (cubic-bezier(0.25, 1, 0.5, 1)).
 *   - Decimal values count smoothly; integer values use a step
 *     function so fractional digits don't flicker.
 *   - Applies only on first mount, not on re-render.
 *
 * Respects `prefers-reduced-motion` — snaps instantly to target.
 * ──────────────────────────────────────────────────────────── */

const EASE_OUT_QUART = (t) => 1 - Math.pow(1 - t, 4);

const useCountUp = (target, { duration = 600, decimals = 0 } = {}) => {
  const [value, setValue] = useState(0);
  // Ref guards against React 18 StrictMode double-invoke and keeps the
  // animation idempotent — we only ever count up once per consumer mount.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return undefined;
    startedRef.current = true;

    if (typeof target !== 'number' || Number.isNaN(target)) {
      setValue(target);
      return undefined;
    }

    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced || duration <= 0) {
      setValue(target);
      return undefined;
    }

    let raf;
    const start = performance.now();
    const step = (now) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / duration);
      const eased = EASE_OUT_QUART(t);
      const next = target * eased;
      setValue(decimals === 0 ? Math.round(next) : next);
      if (t < 1) raf = requestAnimationFrame(step);
      else setValue(target);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, decimals]);

  return value;
};

export default useCountUp;
